import CustomButton from 'Components/CustomButton'
import CustomLoadingButton from 'Components/CustomLoadingButton'
import DeleteButton from 'Components/DeleteButton'
import ProductTariffFieldError from 'Components/ProductTariffFieldErrors'
import StyledLabel from 'Components/StyledLabel'
import TranslationInput from 'Components/TranslationInput'
import { SELECTED_ZONE_DEFINITION_PARAMETER_KEY } from 'Services/ProductManagement/Features/ZoneDefinition/Hooks/useZoneDefinitionsWithParams'
import type { ZoneDefinition } from 'Services/ProductManagement/Features/ZoneDefinition/types'
import {
  useCreateSingleZoneDefinitionMutation,
  useUpdateSingleZoneDefinitionMutation
} from 'Services/ProductManagement/Features/ZoneDefinition/zoneDefinitionApi'
import type { Translation } from 'Store'
import type { ProductTariffError, ProductTariffViolation } from 'Util/ApiClient'
import getTranslation from 'Util/getTranslation'
import SnackbarUtils from 'Util/Snackbar'
import { appendQueryParamToUrl } from 'Util/url'
import AddIcon from '@mui/icons-material/Add'
import {
  Autocomplete,
  Box,
  Chip,
  Divider,
  FormControl,
  Grid,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  useTheme
} from '@mui/material'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { t } from 'ttag'

const StyledTableHeadCell = styled(TableCell)(() => ({
  fontWeight: 600,
  padding: '12px 24px'
}))

const StyledTableCell = styled(TableCell)(() => ({
  borderBottom: 'none',
  padding: 12
}))

const StyledChip = styled(Chip)(() => ({
  fontWeight: 700,
  fontSize: 13,
  lineHeight: 18,
  marginRight: 8
}))

type FieldErrors = {
  [key: string]: string[]
}

interface TranslationObject {
  value: string
  translations: Translation[]
}

const fieldErrorsDefaultState: FieldErrors = {
  translations: [],
  zones: [],
  specialZones: []
}

interface ZoneDefinitionFormProps {
  zoneDefinition?: ZoneDefinition
  successBaseUrl: string
}

export function ZoneDefinitionForm({
  zoneDefinition,
  successBaseUrl
}: Readonly<ZoneDefinitionFormProps>) {
  const navigate = useNavigate()
  const theme = useTheme()

  const isEdit = !!zoneDefinition

  const [createSingleZoneDefinition, { isLoading: isCreateLoading }] =
    useCreateSingleZoneDefinitionMutation()
  const [updateSingleZoneDefinition, { isLoading: isUpdateLoading }] =
    useUpdateSingleZoneDefinitionMutation()

  const [fieldErrors, setFieldErrors] = useState(fieldErrorsDefaultState)
  const [zoneDefinitionDetails, setZoneDefinitionDetails] = useState<Partial<ZoneDefinition>>(
    (isEdit && zoneDefinition) || {
      translations: {
        '': [
          {
            locale: 'fi',
            value: '',
            description: '',
            name: '',
            short_name: '',
            additional_name: ''
          },
          {
            locale: 'sv',
            value: '',
            description: '',
            name: '',
            short_name: '',
            additional_name: ''
          },
          {
            locale: 'en',
            value: '',
            description: '',
            name: '',
            short_name: '',
            additional_name: ''
          }
        ]
      },
      zones: [],
      special_zones: []
    }
  )
  const [newSpecialZone, setNewSpecialZone] = useState<string[]>([])

  // Transform translations field data to an array of objects so the key of the translation
  // can be easily edited. If there are no translations, create an empty array with default values
  // as placeholder for input fields.
  const initialTranslationObjects = Object.entries(
    zoneDefinitionDetails.translations ?? {
      '': [
        {
          locale: 'fi',
          value: ''
        },
        {
          locale: 'sv',
          value: ''
        },
        {
          locale: 'en',
          value: ''
        }
      ]
    }
  ).map(([key, translation]) => {
    return {
      value: key,
      translations: translation
    }
  })
  const [translationObjects, setTranslationObjects] =
    useState<TranslationObject[]>(initialTranslationObjects)

  /**
   * Generate all possible zone combinations from the translations automatically.
   * Ex. with zone translation values: ["20", "21", "22"] the possible zone combinations are:
   * [["20"], ["20", "21"], ["20", "21", "22"], ["21"], ["21", "22"], ["22"]]
   *
   * @param translations
   * @returns {string[][]} An array of zone combinations
   */
  const generateZones = (translations: TranslationObject[]): string[][] => {
    const zoneValues = translations.map(translation => translation.value)
    const zones: string[][] = []

    for (let i = 0; i < zoneValues.length; i++) {
      for (let j = i; j < zoneValues.length; j++) {
        zones.push(zoneValues.slice(i, j + 1))
      }
    }
    return zones
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: suppress generateZones
  useEffect(() => {
    setZoneDefinitionDetails(prevState => ({
      ...prevState,
      zones: generateZones(translationObjects)
    }))
  }, [translationObjects])

  const sortTranslationObjects = () => {
    setTranslationObjects(prevState => {
      const sortedTranslationObjects = [...prevState].sort((a, b) => a.value.localeCompare(b.value))
      return sortedTranslationObjects
    })
  }

  const handleTranslationChange = (translations: Translation[], translation: TranslationObject) => {
    setTranslationObjects(prevState => {
      const updatedTranslationObjects = [...prevState]
      const translationIndex = prevState.findIndex(
        translationObject =>
          translationObject.value === translation.value &&
          translationObject.translations === translation.translations
      )
      updatedTranslationObjects[translationIndex] = {
        value: translation.value,
        translations
      }
      return updatedTranslationObjects
    })
  }

  const handleTranslationKeyChange = (value: string, translation: TranslationObject) => {
    setTranslationObjects(prevState => {
      const updatedTranslationObjects = [...prevState]
      const translationIndex = prevState.findIndex(
        translationObject =>
          translationObject.value === translation.value &&
          translationObject.translations === translation.translations
      )
      updatedTranslationObjects[translationIndex] = {
        value,
        translations: prevState[translationIndex].translations
      }
      return updatedTranslationObjects
    })
  }

  const addNewZoneRow = () => {
    setTranslationObjects(prevState => [
      ...prevState,
      {
        value: '',
        translations: [
          {
            locale: 'fi',
            value: ''
          },
          {
            locale: 'sv',
            value: ''
          },
          {
            locale: 'en',
            value: ''
          }
        ]
      } as TranslationObject
    ])
  }

  const removeZoneRow = (index: number) => {
    setTranslationObjects(prevState => {
      const updatedTranslationObjects = [...prevState]
      updatedTranslationObjects.splice(index, 1)
      return updatedTranslationObjects
    })
  }

  const handleNewZoneCombination = (
    value: {
      value: string
      label: string
    }[]
  ) => {
    const zone = value.map(({ value }) => value)

    // Sort the selected zone values in ascending order
    zone.sort((a, b) => a.localeCompare(b))
    setNewSpecialZone(zone)
  }

  const addNewSpecialZone = () => {
    setZoneDefinitionDetails(prevState => ({
      ...prevState,
      special_zones: [...(prevState.special_zones ?? []), newSpecialZone]
    }))
    setNewSpecialZone([])
  }

  const handleDeleteZone = (index: number, field: keyof ZoneDefinition) => {
    setZoneDefinitionDetails(prevState => {
      const zoneField = prevState[field] as string[][]
      const updatedZones = [...zoneField]
      updatedZones.splice(index, 1)
      return { ...prevState, [field]: updatedZones }
    })
  }

  const getZoneTranslation = (zoneMap: string[]) => {
    return zoneMap
      .map(
        zoneId =>
          getTranslation(
            translationObjects.find(translationObject => translationObject.value === zoneId)
              ?.translations,
            'fi'
          ).value
      )
      .join('+')
  }

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    // Transform the translation objects back to the original format
    const formattedTranslations = translationObjects.reduce(
      (translationRecord, translationObject) => {
        translationRecord[translationObject.value] = translationObject.translations
        return translationRecord
      },
      {} as Record<string, Translation[]>
    )

    const zoneDefinitionData = {
      ...zoneDefinitionDetails,
      translations: formattedTranslations
    }

    if (zoneDefinition) {
      updateSingleZoneDefinition({
        ...zoneDefinitionData,
        id: zoneDefinition.id
      })
        .unwrap()
        .then(() => {
          SnackbarUtils.success(t`Zone definition successfully updated.`)
          navigate(successBaseUrl)
        })
        .catch(({ data: errorResponse }: { data: ProductTariffError }) => {
          const { violations } = errorResponse
          if (violations && violations.length > 0) {
            setValidationErrors(violations)
          }
          SnackbarUtils.error(t`There was an error updating the zone definition.`)
        })
      return
    }

    createSingleZoneDefinition(zoneDefinitionData)
      .unwrap()
      .then((zoneDefinitionResponse: ZoneDefinition) => {
        SnackbarUtils.success(t`Zone definition successfully created.`)
        navigate(
          appendQueryParamToUrl(
            successBaseUrl,
            SELECTED_ZONE_DEFINITION_PARAMETER_KEY,
            zoneDefinitionResponse.id
          )
        )
      })
      .catch(({ data: errorResponse }: { data: ProductTariffError }) => {
        const { violations } = errorResponse
        if (violations && violations.length > 0) {
          setValidationErrors(violations)
        }
        SnackbarUtils.error(t`There was an error creating the zone definition.`)
      })
  }

  function setValidationErrors(violations: ProductTariffViolation[]) {
    setFieldErrors(() => {
      // Reset the field errors to default state, so that the old errors won't be shown
      const updatedFieldErrors = { ...fieldErrorsDefaultState }
      for (const violation of violations) {
        updatedFieldErrors[violation.field] = [violation.message]
      }
      return updatedFieldErrors
    })
  }

  const isSubmitLoading = isCreateLoading || isUpdateLoading

  return (
    <Grid container spacing={2} mb={4}>
      <Grid size={12}>
        <Box component="form" onSubmit={handleSubmit}>
          <Box>
            <StyledLabel>{t`Zone codes`}</StyledLabel>
            <TableContainer component={Box} sx={{ mt: 4 }}>
              <Table sx={{ width: 700 }}>
                <TableHead>
                  <TableRow>
                    <StyledTableHeadCell sx={{ width: 120 }}>{t`Code`}</StyledTableHeadCell>
                    <StyledTableHeadCell sx={{ borderLeft: `1px solid ${theme.palette.divider}` }}>
                      {'FI'}
                    </StyledTableHeadCell>
                    <StyledTableHeadCell>{'SV'}</StyledTableHeadCell>
                    <StyledTableHeadCell>{'EN'}</StyledTableHeadCell>
                    <StyledTableHeadCell>{t`Delete`}</StyledTableHeadCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {translationObjects.map((translationObject, index) => (
                    <TableRow
                      key={`zone-fields-${index}-${translationObject.translations.join('')}`}
                    >
                      <StyledTableCell>
                        <TextField
                          name="code"
                          required
                          label={t`Code`}
                          size="small"
                          value={translationObject.value}
                          onChange={e =>
                            handleTranslationKeyChange(e.target.value, translationObject)
                          }
                          inputProps={{ onBlur: sortTranslationObjects }}
                        />
                      </StyledTableCell>
                      <StyledTableCell
                        sx={{
                          borderLeft: `1px solid ${theme.palette.divider}`
                        }}
                      >
                        <TranslationInput
                          translations={translationObject.translations ?? []}
                          field="value"
                          label={'FI'}
                          locale={'fi'}
                          setTranslations={translations =>
                            handleTranslationChange(translations, translationObject)
                          }
                          size="small"
                          required={true}
                        />
                      </StyledTableCell>
                      <StyledTableCell>
                        <TranslationInput
                          translations={translationObject.translations ?? []}
                          field="value"
                          label={'SV'}
                          locale={'sv'}
                          setTranslations={translations =>
                            handleTranslationChange(translations, translationObject)
                          }
                          size="small"
                          required={true}
                        />
                      </StyledTableCell>
                      <StyledTableCell>
                        <TranslationInput
                          translations={translationObject.translations ?? []}
                          field="value"
                          label={'EN'}
                          locale={'en'}
                          setTranslations={translations =>
                            handleTranslationChange(translations, translationObject)
                          }
                          size="small"
                          required={true}
                        />
                      </StyledTableCell>
                      <StyledTableCell sx={{ minWidth: 150 }}>
                        <DeleteButton onClick={() => removeZoneRow(index)} size="small">
                          {t`Remove row`}
                        </DeleteButton>
                      </StyledTableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Box mt={1}>
              <ProductTariffFieldError error={fieldErrors.translations} identifier="translations" />
            </Box>
            <Box mt={1}>
              <CustomButton
                size="small"
                variant="text"
                startIcon={<AddIcon />}
                onClick={addNewZoneRow}
              >
                {t`Add row`}
              </CustomButton>
            </Box>
          </Box>
          {/* Special zones select */}
          <Box mt={4}>
            <StyledLabel>{t`Special zones`}</StyledLabel>
            <Box maxWidth={650}>
              <Divider sx={{ my: 2 }} />
              <Box display="flex" gap={1} flexWrap="wrap" paddingY={1}>
                {zoneDefinitionDetails.special_zones &&
                zoneDefinitionDetails.special_zones.length > 0 ? (
                  zoneDefinitionDetails.special_zones?.map((zone, index) => (
                    <StyledChip
                      key={`special-zone-${index}-${zone.join('')}`}
                      label={getZoneTranslation(zone)}
                      onDelete={() => handleDeleteZone(index, 'special_zones')}
                    />
                  ))
                ) : (
                  <Box
                    display="flex"
                    alignItems="center"
                    width="100%"
                    sx={{
                      pl: 2,
                      color: 'text.secondary',
                      height: 56,
                      backgroundColor: theme.palette.background.paper
                    }}
                  >{t`No added special zones`}</Box>
                )}
              </Box>
              <Divider sx={{ my: 2 }} />
            </Box>
            <Box display="flex" alignItems="center">
              <FormControl sx={{ minWidth: 240, mt: 2 }}>
                <Autocomplete
                  size="small"
                  multiple
                  options={translationObjects.map(({ value, translations }) => ({
                    value,
                    label: getTranslation(translations, 'fi').value
                  }))}
                  getOptionLabel={option => option.label}
                  value={(newSpecialZone ?? []).map(zone => ({
                    value: zone,
                    label: getTranslation(
                      translationObjects.find(translationObject => translationObject.value === zone)
                        ?.translations,
                      'fi'
                    ).value
                  }))}
                  isOptionEqualToValue={(option, value) => option.value === value.value}
                  renderInput={params => <TextField {...params} label={t`Select special zone`} />}
                  onChange={(_, newValue) => handleNewZoneCombination(newValue)}
                  disableCloseOnSelect={true}
                />
                <ProductTariffFieldError
                  error={fieldErrors.specialZones}
                  identifier="special_zones"
                />
              </FormControl>
              <CustomButton
                onClick={addNewSpecialZone}
                disabled={!newSpecialZone.length}
                startIcon={<AddIcon />}
                sx={{ mt: 2, ml: 2 }}
              >{t`Add special zone`}</CustomButton>
            </Box>
          </Box>
          <Box mt={4}>
            <CustomLoadingButton
              isLoading={isSubmitLoading}
              type="submit"
              size="large"
              iconPosition="end"
            >
              {t`Save zone definition`}
            </CustomLoadingButton>
          </Box>
        </Box>
      </Grid>
    </Grid>
  )
}
